/**
 * Admin Apic client provider.
 *
 * Constructs a ApicClient from gen/client/react configured with:
 *   - baseUrl = window.location.origin
 *   - config.headers carrying the current Bearer token from AuthContext
 *   - onUnauthorized wired to the existing re-auth (logout) flow
 *
 * Admin endpoints are spec'd as auth:"none" so applyHTTPAuth does NOT inject
 * the Authorization header for them. Instead, the Bearer is set via
 * config.headers which the generated client applies to EVERY request, including
 * auth:"none" endpoints. This satisfies enforceAdminMultiTenant on the backend.
 *
 * Recreate the client when the token changes (useMemo keyed on token).
 *
 * FE TEMPLATE NOTE (for the other 19 resources):
 *   1. Import `useAdminGenClient` here.
 *   2. Call `const client = useAdminGenClient()` in the page component.
 *   3. Call `client.adminList<Resource>({ tenantId })` etc.
 *   4. Map AdminSubjectItem (camelCase) → PAMSubject (snake_case) if needed,
 *      or use the generated types directly and update the column defs.
 */
import { useMemo } from 'react'
import {
  ApicClient,
  type ClientConfig,
} from '../../../gen/client/react/client'
import { useAuth } from '../auth/AuthContext'

/**
 * Returns a ApicClient instance configured with the current Bearer token
 * from AuthContext injected via config.headers. The client is recreated
 * automatically when the token changes.
 *
 * The `onUnauthorized` callback triggers logout so the user is redirected to
 * the login page when the backend returns 401 (expired / revoked token).
 */
export function useAdminGenClient(): ApicClient {
  const { token, clearAuth } = useAuth()

  return useMemo((): ApicClient => {
    const config: ClientConfig = {
      ...baseClientConfig(token),
      // On 401, soft-clear the auth state (does NOT hard-redirect) so the
      // in-app ProtectedRoute sign-in card is shown in the same window.
      // The user can re-authenticate via a silent SSO round-trip if the server
      // session is still valid. Use the Logout button for a full server logout.
      onUnauthorized: (): void => {
        clearAuth()
      },
    }

    return new ApicClient(config)
  }, [token, clearAuth])
}

/**
 * Identical to useAdminGenClient, except onUnauthorized is omitted entirely.
 *
 * PAM activation deliberately returns 401 to mean "this call needs a step-up
 * (privileged) session" — a recoverable, expected outcome, not a dead session.
 * useAdminGenClient's onUnauthorized calls clearAuth() synchronously as part of
 * the same request/response cycle, before the caller's own catch block runs,
 * which wipes the token and makes ProtectedRoute swap the whole page to its
 * generic sign-in card before the step-up prompt UI can render. Route ONLY
 * admin/self-service activate() calls through this client; other calls should
 * keep triggering a genuine sign-out on a real dead-session 401.
 */
export function useAdminGenClientNoAutoLogout(): ApicClient {
  const { token } = useAuth()

  return useMemo((): ApicClient => {
    return new ApicClient(baseClientConfig(token))
  }, [token])
}

function baseClientConfig(token: string | null): ClientConfig {
  const baseUrl =
    typeof window !== 'undefined' ? window.location.origin : 'http://localhost'

  return {
    baseUrl,
    // Inject Bearer via config.headers so it is sent on every request,
    // including auth:"none" admin endpoints where applyHTTPAuth is a no-op.
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    // Also set bearerToken for any jwt-mode endpoints on the same client.
    bearerToken: token ?? undefined,
  }
}
